// TODO - remove lint disable when editing this file
/* eslint-disable */
import {
  SelectionState,
  Modifier,
  ContentBlock,
  genKey,
  convertFromHTML,
} from 'draft-js'
import cheerio from 'cheerio'
import { OrderedMap, Map } from 'immutable'
import punycode from 'punycode/'

export const InsertTypes = {
  AFTER: 'after',
  BEFORE: 'before',
  AT: 'at',
}

/**
 * Return a selection located at the end of the end block
 * @param {*} contentState - DraftJS content state
 * @returns ContentState
 */
export function getEndOfTextSelection(contentState) {
  const lastBlock = contentState.getLastBlock()
  const lastKey = lastBlock.getKey()
  const lastBlockLength = lastBlock.getLength()

  return new SelectionState({
    anchorKey: lastKey,
    anchorOffset: lastBlockLength,
    focusKey: lastKey,
    focusOffset: lastBlockLength,
    isBackward: false,
  })
}
/**
 * Insert a ContentBlock into ContentState
 * @param {*} contentState - DraftJS content state
 * @param {*} targetSelection - DraftJS SelectionState insert position
 * @param {*} contentBlock - ContentBlock instance to be inserted
 */
export function insertContentBlock(
  contentState,
  targetSelection,
  contentBlock,
  insertType = InsertTypes.AFTER
) {
  const { newContentState, insertionTarget } = getInsertionTarget(
    contentState,
    targetSelection,
    insertType
  )
  const arrayOfBlocks = Array.isArray(contentBlock)
    ? contentBlock
    : [contentBlock]

  if (arrayOfBlocks.length === 0) {
    throw new Error('insertContentBlock called with empty block')
  }

  const asTypedBlock = Modifier.setBlockType(
    newContentState,
    insertionTarget,
    arrayOfBlocks[0].getType()
  )

  const fragment = createFragmentFromArray(arrayOfBlocks)
  const withUpdatedFragment = Modifier.replaceWithFragment(
    asTypedBlock,
    insertionTarget,
    fragment
  )

  const newBlocks = getInsertedBlockes(
    withUpdatedFragment,
    insertionTarget,
    arrayOfBlocks
  )

  const depth = arrayOfBlocks[0].get('depth')
  if (depth === 0) {
    return {
      contentState: withUpdatedFragment,
      contentBlocks: newBlocks,
    }
  }

  // A magic to set depth as Modifier.replaceWithFragment does not do it
  const blockMap = withUpdatedFragment.getBlockMap()
  const last = newBlocks.length - 1
  const targetBlock = newBlocks[last]
  newBlocks[last] = targetBlock.merge({ depth })

  return {
    contentState: withUpdatedFragment.merge({
      blockMap: blockMap.set(targetBlock.getKey(), newBlocks[last]),
    }),
    contentBlocks: newBlocks,
  }
}

export function insertEllpsisBlock(
  contentState,
  targetSelection,
  ellipsisData,
  insertType = InsertTypes.AFTER
) {
  const { type } = ellipsisData
  if (!type || typeof type !== 'string') {
    throw new TypeError(
      'insertEllpsisBlock supplied with wrong ellipsisData instance'
    )
  }
  const ellipsisType = `ellipsis.${type}`

  let asAtomicBlock
  let { newContentState, insertionTarget } = getInsertionTarget(
    contentState,
    targetSelection,
    insertType
  )
  if (insertType === InsertTypes.AT) {
    const blockForReplace = newContentState.getBlockForKey(
      insertionTarget.getAnchorKey()
    )
    insertionTarget = insertionTarget.merge({
      anchorOffset: 0,
      focusKey: insertionTarget.getAnchorKey(),
      focusOffset: blockForReplace.getLength(),
      isBackward: false,
      hasFocus: true,
    })
  }

  asAtomicBlock = Modifier.setBlockType(
    newContentState,
    insertionTarget,
    'atomic'
  )
  const fragmentArray = [
    new ContentBlock({
      key: genKey(),
      type: 'atomic',
      text: '',
      data: Map({
        ellipsisType,
        ans: ellipsisData,
      }),
    }),
  ]

  const fragment = createFragmentFromArray(fragmentArray)

  const withUpdatedFragment = Modifier.replaceWithFragment(
    asAtomicBlock,
    insertionTarget,
    fragment
  )

  return {
    contentState: withUpdatedFragment,
    contentBlocks: getInsertedBlockes(
      withUpdatedFragment,
      insertionTarget,
      fragmentArray
    ),
  }
}

export function clearEllpsisBlock(contentState, block) {
  const blockKey = block.getKey()
  let selection = SelectionState.createEmpty(blockKey)
  selection = selection.merge({ focusOffset: block.getLength() })
  // Remove entire block data for the current block
  contentState = Modifier.setBlockData(contentState, selection, Map())
  contentState = Modifier.setBlockType(contentState, selection, 'unstyled')
  contentState = Modifier.removeRange(contentState, selection, 'forward')
  return contentState
}

export function insertEmptyBlock(
  contentState,
  targetSelection,
  insertType = InsertTypes.AFTER,
  type = 'unstyled',
  data = Map()
) {
  const { newContentState, insertionTarget } = getInsertionTarget(
    contentState,
    targetSelection,
    insertType
  )

  const asEmptyBlock = Modifier.setBlockType(
    newContentState,
    insertionTarget,
    type
  )
  let emptyBlock = new ContentBlock({
    key: genKey(),
    type: type,
    text: '',
    data,
  })
  const fragmentArray = [emptyBlock]
  const fragment = createFragmentFromArray(fragmentArray)
  const withUpdatedFragment = Modifier.replaceWithFragment(
    asEmptyBlock,
    insertionTarget,
    fragment
  )

  const newBlocks = getInsertedBlockes(
    withUpdatedFragment,
    insertionTarget,
    fragmentArray
  )

  return {
    contentState: withUpdatedFragment,
    contentBlocks: newBlocks,
  }
}

export function createEmptyBlock(type = 'unstyled', data = Map()) {
  return new ContentBlock({
    key: genKey(),
    text: '',
    type,
    data,
  })
}

export function insertCodeBlock(
  contentState,
  text,
  syntax,
  targetSelection,
  insertType = InsertTypes.AFTER
) {
  const { newContentState, insertionTarget } = getInsertionTarget(
    contentState,
    targetSelection,
    insertType
  )

  const asEmptyBlock = Modifier.setBlockType(
    newContentState,
    insertionTarget,
    'code-block'
  )
  const fragmentArray = [
    new ContentBlock({
      key: genKey(),
      type: 'code-block',
      text,
    }).merge({
      data: Map({
        syntax: syntax || 'html',
      }),
    }),
  ]

  const fragment = createFragmentFromArray(fragmentArray)

  return Modifier.replaceWithFragment(asEmptyBlock, insertionTarget, fragment)
}

export function contextBlocksFromPlainText(text) {
  const textWithoutEmptyBlocks = text.trim().replace(/\n+/g, '\n')
  const paras = textWithoutEmptyBlocks.split('\n')
  const html = paras.map((text) => `<p>${text}</p>`).join('')
  const blocksFromHTML = convertFromHTML(html)
  return blocksFromHTML && blocksFromHTML.contentBlocks
}

// The block keys are changed after using Modifier.replaceWithFragment, so use this method to get the new blocks in case needed
export function getInsertedBlockes(
  contentState,
  insertionTarget,
  originalBlocks
) {
  let blockKey = insertionTarget.getAnchorKey()
  let block = null
  return originalBlocks.map(() => {
    block = blockKey ? contentState.getBlockForKey(blockKey) : null
    blockKey = blockKey ? contentState.getKeyAfter(blockKey) : null
    return block
  })
}

export function getInsertionTarget(
  contentState,
  targetSelection,
  insertType = InsertTypes.AFTER
) {
  let newContentState
  let insertionTarget
  // If no target selection, use current contentState's selection after
  if (!targetSelection) {
    targetSelection = contentState.getSelectionAfter()
  }

  if (insertType === InsertTypes.AT) {
    // Don't need to split if insert type is at
    newContentState = contentState
    insertionTarget = targetSelection
  } else {
    newContentState = Modifier.splitBlock(contentState, targetSelection)
    if (insertType === InsertTypes.BEFORE) {
      // Modifier.splitBlock creates new block after targetSelection, so need to swap the key and data
      // as we expect inserting before should create new block before the targetSelection
      const blockMap = newContentState.getBlockMap()
      let blockAbove = blockMap.get(targetSelection.getAnchorKey())
      let blockBelow = blockMap.get(
        newContentState.getSelectionAfter().getAnchorKey()
      )
      const newKey = blockBelow.getKey()
      const newData = blockBelow.getData()
      const blocksBefore = blockMap.takeUntil((v) => v === blockAbove)
      const blocksAfter = blockMap.skipUntil((v) => v === blockBelow).rest()

      blockBelow = blockBelow.merge({
        key: blockAbove.getKey(),
        data: blockAbove.getData(),
      })
      blockAbove = blockAbove.merge({
        key: newKey,
        data: newData,
      })
      const newBlocks = blocksBefore.concat(
        [
          [blockAbove.getKey(), blockAbove],
          [blockBelow.getKey(), blockBelow],
        ],
        blocksAfter
      )

      newContentState = newContentState.merge({
        blockMap: newBlocks,
        selectionAfter: newContentState.getSelectionAfter().merge({
          anchorKey: blockAbove.getKey(),
          anchorOffset: blockAbove.getLength(),
          focusKey: blockAbove.getKey(),
          focusOffset: blockAbove.getLength(),
        }),
      })
    }
    insertionTarget = newContentState.getSelectionAfter()
  }

  return { newContentState, insertionTarget }
}

export function createFragmentFromArray(blocks) {
  return OrderedMap(
    blocks.map(function (block) {
      return [block.getKey(), block]
    })
  )
}

export function sanitizeText(text = '') {
  return text
    .replace(/<br\s*\/?>/g, '<br/>') // Sanitize br's i.e. <br> => <br/>
    .replace(/<br\/>(\s*?<br\/>)*/g, '<br/>') // Condense multiple breaks i.e. <br/> <br/> => <br/>
    .replace(/\n/g, '') // All other \n in a  HTML are considered as a debug niceiety
}

const cheerioUnwrap = (ch, element) => {
  element.contents().each(function () {
    ch(this).insertBefore(element)
  })
  element.remove()
}

export function sanitizeHTML(html) {
  const ch = cheerio.load(html)
  if (!ch) {
    return null
  }
  ch('font, span, section').each(function () {
    cheerioUnwrap(ch, ch(this))
  })

  const tagsToBeRemoved =
    'meta, xml, style, script, title, link, content-tools, content-comment, contenttools, div.handles, br, hr, o\\:p'
  ch(tagsToBeRemoved).remove()

  ch('div')
    .has('>p')
    .each(function (i, elem) {
      cheerioUnwrap(ch, ch(elem))
    })

  // Get rid of wrapped divs
  // div into div cause Draft to put everything in one block
  let hasWrappedDiv
  do {
    hasWrappedDiv = false
    ch('div')
      .has('>div')
      .each(function (i, elem) {
        hasWrappedDiv = true
        cheerioUnwrap(ch, ch(elem))
      })
  } while (hasWrappedDiv)

  // Remove empty stuff
  ch('div, p, span').each((_, elem) => {
    if (ch(elem).contents().length === 0) {
      ch(elem).remove()
    }
  })

  // Convert outermost divs into p
  // this helps with mixed lists / paragraphs under div
  const inlineTags = [
    'a',
    'span',
    'font',
    'b',
    'em',
    'i',
    'strong',
    'small',
    'big',
  ]
  const contents = ch('body').contents()
  contents.each(function (i, elem) {
    const chEl = ch(elem)
    const html = chEl.html()
    if (html) {
      // Handle single child
      if (contents.length === 1) {
        // Keep link
        if (elem.name !== 'a') {
          chEl.replaceWith(ch(`<p>${html}</p>`))
        }
        // Exclude tags listed in inlineTags if they are multiple children
      } else if (!~inlineTags.indexOf(elem.name)) {
        chEl.replaceWith(ch(`<p>${html}</p>`))
      }
      // Don't remove text node
    } else if (elem.type !== 'text') {
      chEl.remove()
    }
  })

  // Keep only allowed attributes
  const validAttributes = ['href']
  ch('*').each((_, el) => {
    const attributesToBeDeleted = Object.keys(el.attribs).filter(
      (name) => !~validAttributes.indexOf(name)
    )
    attributesToBeDeleted.forEach((name) => ch(el).removeAttr(name))
  })

  let text = ch('body').html()
  // Ported from Ellipsis
  // And it seems to replace some spaces with a freaking return in Open Office!
  if (escape(text).indexOf('%0A') > -1) {
    text = unescape(
      escape(text)
        .replace(/(%0A)+/g, ' ')
        .replace(/(%20)+/g, ' ')
    ).trim()
  }
  return text
}

export function contextBlocksFromHTML(html) {
  const sanitized = sanitizeHTML(html)
  if (!sanitized) {
    return null
  }
  const blocksFromHTML = convertFromHTML(sanitized)
  return blocksFromHTML && blocksFromHTML.contentBlocks
}

/**
 *
 * @param {String} plainText - plain text
 * @param {{unit: 'words'|'chars', value: number, linesPerInch: number}} lineSettings - lineSettings object for a specific website
 */
export function getInches(plainText, lineSettings) {
  const lineCount = getLineCount(plainText, lineSettings)
  return parseFloat(
    Math.round((lineCount / lineSettings.linesPerInch) * 1000) / 1000
  ).toFixed(3)
}

/**
 *
 * @param {String} plainText - plain text
 * @param {{unit: 'words'|'chars', value: number}} lineSettings - lineSettings object for a specific website
 */
export function getLineCount(plainText, lineSettings) {
  const blockLines = plainText.split('\n')

  let lineCount = 0
  blockLines.forEach((blockLine) => {
    let blockCount
    if (lineSettings.unit === 'words') {
      // When there are no words, don't count it as a line
      blockCount = (blockLine.match(/\S+/g) || []).length
    } else {
      // line count configured in characters
      blockCount = blockLine.length
    }

    lineCount += Math.ceil(blockCount / lineSettings.value)
  })
  return lineCount
}

/**
 *
 * @param {String} plainText
 */
export function getWordCount(plainText) {
  const regex = /(?:\r\n|\r|\n)/g // new line, carriage return, line feed
  const cleanString = plainText.replace(regex, ' ').trim() // replace above characters w/ space
  const wordArray = cleanString.match(/\S+/g) // matches words according to whitespace
  return wordArray ? wordArray.length : 0
}

/**
 *
 * @param {String} plainText
 */
export function getCharacterCount(plainText) {
  const decodeUnicode = (str) => punycode.ucs2.decode(str) // Generates array containing the numeric code point values of each Unicode symbol in the string
  const regex = /(?:\r\n|\r|\n)/g // new line, carriage return, line feed
  const cleanString = plainText.replace(regex, '').trim() // replace above characters w/ nothing
  return decodeUnicode(cleanString).length
}

export function getTextWithoutComments(contentState) {
  return contentState
    .getBlockMap()
    .map((block) => {
      if (block) {
        const blockText = block.getText()
        return block.getCharacterList().reduce((acc, charMeta, index) => {
          // skip inline comment
          return (
            acc + (charMeta.hasStyle('INLINECOMMENT') ? '' : blockText[index])
          )
        }, '')
      }
      return ''
    })
    .join('\n')
}

// We need to know to hide the placeholder even when the first block is not text
export function shouldHidePlaceholder(editorState) {
  const contentState = editorState.getCurrentContent()
  const firstBlockType = contentState.getBlockMap().first().getType()
  return contentState.hasText() || firstBlockType !== 'unstyled'
}

/**
 * remove the unprintable characters from the text
 * @param {string} text - block/paragraph of text
 */
export function removeUnprintableText(text) {
  // error handling
  if (!text) return

  // List of unprintable characters that causes error to Send to Print
  // Add character(s) with the purpose in mind
  const re = /[\u2009\u200B\u2060]/g
  return text.replace(re, '')
}

/**
 * contains open/close single/double and a lower double quotation marks
 * https://unicode-table.com/en/sets/quotation-marks/
 * @param {string} text
 */
export function hasCurlyQuotationMark(text) {
  return /[\u2018\u2019\u201C\u201D\u201E\u201F]/g.test(text)
}